/** Lining laps up by where they were on track, not how far they had gone.
 *
 * A lap's `dist` is integrated from its own speed, so two laps reach the same
 * metre mark at different places: a wider line is a longer lap, and a slide or
 * a run of dropped frames moves the car further than its speed says. Over 141
 * real lap pairs, "the same distance" was a median 2.9 m apart on track and
 * 64 m at p99 — so a time delta taken at equal distance compared the laps at
 * different corners, and map dots meant to sit side by side did not.
 *
 * The fix is one shared axis: the REFERENCE lap's own distance. Every other lap
 * is walked along the reference's driven path and each of its samples is given
 * the reference distance of the point it was level with (the perpendicular
 * projection onto that path). A lap whose `dist` is replaced by that is lined
 * up with the reference by place, and everything downstream that already works
 * on `dist` — resampling, the delta, the corner report, peak markers, event
 * bands — lines up with it for free.
 *
 * Tracking is sequential and local: each sample is searched for only a little
 * behind and ahead of the last one, so a circuit that crosses itself (Suzuka)
 * or runs back alongside itself cannot capture the car on the wrong branch.
 * When the local search has plainly lost the car — GT7 reset it onto the track,
 * sometimes kilometres away, in one tick — the whole path is searched instead.
 * The axis handed out never runs backwards (a spin, or a rewind, holds it until
 * the car is past where it had been).
 *
 * A lap that cannot be tracked with confidence keeps its own distance, which is
 * what every comparison used before: better an honest old axis than a wrong
 * new one.
 */

import { column } from './columns';
import { interp } from './interp';
import { resampleByDistance } from './resample';
import { roundTo } from './round';
import type { LapEvent, Samples } from './types';

/** Spacing of the reference path the laps are projected onto. The projection
 *  is onto the straight segments between these points, so in the tightest
 *  hairpin (radius ~10 m) a 2.5 m chord is 8 cm off the arc. */
export const PATH_STEP_M = 2.5;
/** How far behind and ahead of the last located point a sample is searched
 *  for — ahead also by twice the distance the lap itself says it covered
 *  since, which covers dropped frames at any speed. At 60 Hz a car moves
 *  under 2 m a tick, so this is several ticks of slack either way. */
export const BEHIND_M = 5;
export const AHEAD_M = 10;
/** A local best further off the path than this has lost the car (a reset, a
 *  rewind) — unless the whole-path search finds nothing better. An excursion
 *  into a run-off area can be this far off and still be where it is, so the
 *  whole-path answer must also be at least twice as close to be believed. */
export const REACQUIRE_M = 50;
/** Where the first sample may be found: around the start of the path (the
 *  lap began at the line, or near it), and this far off it at most. */
export const START_WINDOW_M = 60;
export const START_LATERAL_M = 25;
/** The first and last segments are extended to place a sample before the
 *  reference's first point or past its last — a lap that began a few metres
 *  behind the line, or ran on past the reference's final sample. No further. */
export const EXTEND_MAX_M = 150;
/** A lap is aligned only if at most this share of its samples were off the
 *  path by more than REACQUIRE_M (the pit lane counts as on it: it runs
 *  alongside the circuit). */
export const MAX_OFF_PATH_SHARE = 0.1;
/** Reference segments this many times longer than the distance they span
 *  are resets in the reference itself, not road: never projected onto. */
export const TELEPORT_RATIO = 10;

/** The reference lap's driven line, parametrised by its own distance. */
export class ReferencePath {
  readonly s: number[];
  readonly x: number[];
  readonly z: number[];
  readonly segments: number;
  readonly length: number;
  // Per-segment geometry, computed once: projection runs for every sample
  // of every compared lap. A segment is unusable when there is nothing to
  // project onto — no length, or a reset in the reference.
  private readonly usable: Uint8Array;
  private readonly vx: Float64Array;
  private readonly vz: Float64Array;
  private readonly len2: Float64Array;
  /** How far `f` may run past the segment's ends, as a share of its length. */
  private readonly reach: Float64Array;
  private readonly ds: Float64Array;

  constructor(samples: Samples) {
    const grid = resampleByDistance(samples, PATH_STEP_M, ['pos_x', 'pos_z']);
    this.s = grid['dist'] ?? [];
    this.x = grid['pos_x'] ?? [];
    this.z = grid['pos_z'] ?? [];
    const n = Math.min(this.s.length, this.x.length, this.z.length);
    this.segments = Math.max(0, n - 1);
    this.length = n > 0 ? this.s[n - 1]! : 0;
    this.usable = new Uint8Array(this.segments);
    this.vx = new Float64Array(this.segments);
    this.vz = new Float64Array(this.segments);
    this.len2 = new Float64Array(this.segments);
    this.reach = new Float64Array(this.segments);
    this.ds = new Float64Array(this.segments);
    for (let i = 0; i < this.segments; i++) {
      const vx = this.x[i + 1]! - this.x[i]!;
      const vz = this.z[i + 1]! - this.z[i]!;
      const len2 = vx * vx + vz * vz;
      const ds = this.s[i + 1]! - this.s[i]!;
      if (len2 < 1e-9 || Math.sqrt(len2) > TELEPORT_RATIO * Math.max(ds, 1)) continue;
      this.usable[i] = 1;
      this.vx[i] = vx;
      this.vz[i] = vz;
      this.len2[i] = len2;
      this.reach[i] = EXTEND_MAX_M / Math.sqrt(len2);
      this.ds[i] = ds;
    }
  }

  /** (distance along the path, metres off it) of the nearest point on the
   *  path between distances `lo` and `hi`. */
  project(x: number, z: number, lo = -Infinity, hi = Infinity): [number, number] {
    if (this.segments === 0) return [0, Infinity];
    const first = lo === -Infinity ? 0 : Math.max(0, Math.trunc(lo / PATH_STEP_M) - 1);
    const last =
      hi === Infinity ? this.segments - 1 : Math.min(this.segments - 1, Math.trunc(hi / PATH_STEP_M) + 1);
    const xs = this.x;
    const zs = this.z;
    const ss = this.s;
    const usable = this.usable;
    const vxs = this.vx;
    const vzs = this.vz;
    const len2s = this.len2;
    const reaches = this.reach;
    const dss = this.ds;
    const end = this.segments - 1;
    let bestS = 0;
    let bestD2 = Infinity;
    for (let i = first; i <= last; i++) {
      if (usable[i] === 0) continue;
      const vx = vxs[i]!;
      const vz = vzs[i]!;
      const px = x - xs[i]!;
      const pz = z - zs[i]!;
      let f = (px * vx + pz * vz) / len2s[i]!;
      // Only the ends of the path extend past their segment, and not
      // without limit.
      if (f < 0) {
        f = i === 0 ? Math.max(f, -reaches[i]!) : 0;
      } else if (f > 1) {
        f = i === end ? Math.min(f, 1 + reaches[i]!) : 1;
      }
      const dx = f * vx - px;
      const dz = f * vz - pz;
      const d2 = dx * dx + dz * dz;
      if (d2 < bestD2) {
        bestD2 = d2;
        bestS = ss[i]! + f * dss[i]!;
      }
    }
    return [bestS, Math.sqrt(bestD2)];
  }
}

/** Follows one lap along a reference path, sample by sample. */
export class PathTracker {
  readonly path: ReferencePath;
  private at: number | null = null;
  private own = 0;

  constructor(path: ReferencePath) {
    this.path = path;
  }

  reset(): void {
    this.at = null;
    this.own = 0;
  }

  get started(): boolean {
    return this.at !== null;
  }

  /** (reference distance, metres off the path) for the lap's next sample,
   *  or null when the lap could not be placed at its start.
   *
   * `ownDist` is the lap's own integrated distance at the sample: its
   * growth since the last call says how far ahead to look, and at the
   * start where to.
   */
  locate(x: number, z: number, ownDist: number): [number, number] | null {
    const path = this.path;
    let s: number;
    let off: number;
    if (this.at === null) {
      const found = this.start(x, z, ownDist);
      if (found === null) return null;
      [s, off] = found;
    } else {
      const advance = Math.max(0, ownDist - this.own);
      [s, off] = path.project(x, z, this.at - BEHIND_M, this.at + AHEAD_M + 2 * advance);
      if (off > REACQUIRE_M) {
        const [s2, off2] = path.project(x, z);
        if (off2 * 2 <= off) {
          s = s2;
          off = off2;
        }
      }
    }
    this.at = s;
    this.own = ownDist;
    return [s, off];
  }

  private start(x: number, z: number, ownDist: number): [number, number] | null {
    const path = this.path;
    // Near where the lap's own distance says it is: the line at the start
    // of a lap, somewhere further on for a tracker picked up mid-lap.
    let [s, off] = path.project(x, z, ownDist * 0.9 - START_WINDOW_M, ownDist * 1.1 + START_WINDOW_M);
    if (off <= START_LATERAL_M) return [s, off];
    [s, off] = path.project(x, z);
    if (off > START_LATERAL_M) return null;
    // Found far from the start at the start of a lap — a grid ahead of
    // the line — is believable only in the first half of the path. In the
    // second it is the approach to the line, where the car cannot be at
    // the start of its lap without an axis that runs from minus something.
    if (ownDist < START_WINDOW_M && s > path.length / 2) return null;
    return [s, off];
  }
}

/** A copy of `samples` whose `dist` is the reference path's distance at
 *  each sample, or null when the lap cannot be lined up with confidence. */
export function alignToReference(samples: Samples, path: ReferencePath): Samples | null {
  const xs = column(samples, 'pos_x') ?? [];
  const zs = column(samples, 'pos_z') ?? [];
  const own = column(samples, 'dist') ?? [];
  const n = Math.min(xs.length, zs.length, own.length);
  if (n < 2 || path.segments === 0) return null;
  const tracker = new PathTracker(path);
  const aligned = new Array<number>(n);
  let furthest = -Infinity;
  let offPath = 0;
  for (let k = 0; k < n; k++) {
    const found = tracker.locate(xs[k]!, zs[k]!, own[k]!);
    if (found === null) return null;
    const [s, off] = found;
    if (off > REACQUIRE_M) offPath += 1;
    furthest = Math.max(furthest, s);
    aligned[k] = roundTo(furthest, 2);
  }
  if (offPath > MAX_OFF_PATH_SHARE * n) return null;
  return { ...samples, dist: aligned };
}

/** The lap's events with every `*_dist` moved onto the aligned axis. */
export function remapEvents(
  events: LapEvent[],
  ownDist: readonly number[],
  alignedDist: readonly number[],
): LapEvent[] {
  if (ownDist.length === 0) return events;
  const out: LapEvent[] = [];
  for (const event of events) {
    const moved: LapEvent = { ...event };
    for (const [key, value] of Object.entries(event)) {
      if (key.endsWith('_dist') && typeof value === 'number') {
        moved[key] = roundTo(interp(ownDist, alignedDist, value), 1);
      }
    }
    out.push(moved);
  }
  return out;
}
